/**
 * Utility functions for calculating modified unit stats based on traits and veterancy
 */

import type { Unit } from '../types/unit';
import type { Veterancy, TraitModifier } from '../types/veterancy-cohesion';
import { getTraitModifiersMap, getVeterancyModifiersMap } from '../types/veterancy-cohesion';
import { isTraitActive } from '../damage-calculator/trait-activation';
import { Motion } from '../types/motion';

/**
 * Modified unit stats with base and modified values
 */
export interface ModifiedUnitStats {
  speed: { base: number; modified: number; isModified: boolean };
  roadSpeed: { base: number; modified: number; isModified: boolean };
  rotationTime: { base: number; modified: number; isModified: boolean };
  ecm: { base: number; modified: number; isModified: boolean };
  dodgeBonus?: { base: number; modified: number; isModified: boolean };
  optics: { base: number; modified: number; isModified: boolean };
  airOptics?: { base: number; modified: number; isModified: boolean };
  lowAltOptics?: { base: number; modified: number; isModified: boolean };
  stealth: { base: number; modified: number; isModified: boolean };
}

/**
 * Calculate modified unit stats based on trait overrides and veterancy
 */
export function calculateModifiedUnitStats(
  unit: Unit,
  traitOverrides: Map<string, boolean | undefined>,
  veterancy?: Veterancy
): ModifiedUnitStats {
  // Initialize with base values
  const result: ModifiedUnitStats = {
    speed: { base: unit.speed, modified: unit.speed, isModified: false },
    roadSpeed: { base: unit.roadSpeed, modified: unit.roadSpeed, isModified: false },
    rotationTime: { base: unit.rotationTime, modified: unit.rotationTime, isModified: false },
    ecm: { base: unit.ecm, modified: unit.ecm, isModified: false },
    optics: { base: unit.optics, modified: unit.optics, isModified: false },
    stealth: { base: unit.stealth, modified: unit.stealth, isModified: false },
  };

  // Add optional optics fields if they exist
  if (unit.airOptics !== undefined) {
    result.airOptics = { base: unit.airOptics, modified: unit.airOptics, isModified: false };
  }
  if (unit.lowAltOptics !== undefined) {
    result.lowAltOptics = { base: unit.lowAltOptics, modified: unit.lowAltOptics, isModified: false };
  }

  // Get trait and veterancy modifiers maps (may throw if not initialized)
  let traitModifiersMap: { [traitName: string]: TraitModifier };
  let veterancyModifiersMap: { [key in Veterancy]: any };
  try {
    traitModifiersMap = getTraitModifiersMap();
    veterancyModifiersMap = getVeterancyModifiersMap();
  } catch {
    // Modifiers map not initialized yet, return base stats
    return result;
  }

  // Create a dummy context for trait activation checking
  // Since we're just checking unit stats, we don't need real combat context
  const dummyContext = {
    sourceUnit: unit,
    targetUnit: unit, // Use same unit as target for stat calculation
    distance: 0,
    sourceMotion: Motion.STATIC,
    targetMotion: Motion.STATIC,
    sourceTraitOverrides: traitOverrides,
    targetTraitOverrides: undefined,
  };

  // Apply trait modifiers
  for (const traitName of unit.specialities || []) {
    if (traitName.startsWith('_')) {
      const activationResult = isTraitActive(traitName, dummyContext, true);
      if (activationResult.isActive) {
        const traitModifier = traitModifiersMap[traitName];
        if (!traitModifier) continue;

        // Apply speed modifier (percentage)
        if (traitModifier.speed !== undefined) {
          const speedModifier = traitModifier.speed / 100; // Convert percentage to multiplier
          result.speed.modified = result.speed.modified * (1 + speedModifier);
          result.speed.isModified = true;
          // Also apply to road speed
          result.roadSpeed.modified = result.roadSpeed.modified * (1 + speedModifier);
          result.roadSpeed.isModified = true;
        }

        // Apply rotation speed modifiers (chassis and turret)
        // Faster rotation = lower rotation time (inverse relationship)
        if (traitModifier.chassisRotationSpeed !== undefined) {
          const rotationModifier = traitModifier.chassisRotationSpeed / 100;
          // If rotation speed increases by X%, rotation time decreases by X%
          result.rotationTime.modified = result.rotationTime.modified / (1 + rotationModifier);
          result.rotationTime.isModified = true;
        }
        if (traitModifier.turretRotationSpeed !== undefined) {
          const rotationModifier = traitModifier.turretRotationSpeed / 100;
          // Apply turret rotation speed modifier (also affects rotation time)
          result.rotationTime.modified = result.rotationTime.modified / (1 + rotationModifier);
          result.rotationTime.isModified = true;
        }

        // Apply ECM modifier (additive)
        if (traitModifier.hitRollECM !== undefined) {
          result.ecm.modified = result.ecm.modified + traitModifier.hitRollECM;
          result.ecm.isModified = true;
        }

        // Apply optical strength modifier (additive, same GRU space as unit OpticalStrengths; post-patch NDF uses rescaled bonuses).
        // This affects all optics types (standard, air, low altitude)
        if (traitModifier.opticalStrength !== undefined) {
          result.optics.modified = result.optics.modified + traitModifier.opticalStrength;
          result.optics.isModified = true;
          if (result.airOptics) {
            result.airOptics.modified = result.airOptics.modified + traitModifier.opticalStrength;
            result.airOptics.isModified = true;
          }
          if (result.lowAltOptics) {
            result.lowAltOptics.modified = result.lowAltOptics.modified + traitModifier.opticalStrength;
            result.lowAltOptics.isModified = true;
          }
        }

        // Apply stealth/concealment modifier (additive)
        if (traitModifier.stealth !== undefined) {
          result.stealth.modified = result.stealth.modified + traitModifier.stealth;
          result.stealth.isModified = true;
        }
      }
    }
  }

  // Apply veterancy modifiers if veterancy is specified
  if (veterancy) {
    const veterancyModifier = veterancyModifiersMap[veterancy];
    if (veterancyModifier) {
      // Apply speed modifier (percentage) - only for land units
      if (veterancyModifier.speed !== undefined && unit.movementType === 'land') {
        const speedModifier = veterancyModifier.speed / 100; // Convert percentage to multiplier
        result.speed.modified = result.speed.modified * (1 + speedModifier);
        result.speed.isModified = true;
        // Also apply to road speed
        result.roadSpeed.modified = result.roadSpeed.modified * (1 + speedModifier);
        result.roadSpeed.isModified = true;
      }

      // Apply dodge bonus (for planes) - stored as ['+', number] format
      if (veterancyModifier.dodgeBonus) {
        const [, dodgeValue] = veterancyModifier.dodgeBonus;
        result.dodgeBonus = {
          base: 0,
          modified: dodgeValue,
          isModified: dodgeValue > 0,
        };
      }
    }
  }

  return result;
}

